/**
 * Smart poll — a long-running fetch loop that keeps the workspace store fresh
 * without a real push channel.
 *
 * Four levers that turn naive setInterval into "almost realtime":
 *   1. ETag: we pass the last ETag as If-None-Match and the server returns
 *      304 when nothing changed. A 304 is a few bytes; a 200 is the aggregate
 *      state. No ETag, we treat every response as fresh.
 *   2. Visibility: a polling widget users can't see is waste — pause when
 *      the tab goes hidden, ping once when it comes back.
 *   3. Backoff: if three responses in a row are 304 the cadence doubles up
 *      to a ceiling (default 60s). The ceiling resets the moment we hear a
 *      real change.
 *   4. On-demand ping: after a user action (hatched.track()) the caller
 *      triggers an immediate poll. The backoff resets because we expect a
 *      change.
 *
 * The loop owns no rendering — it only pushes into the shared store via the
 * `onFresh` callback.
 */

export interface SmartPollOptions {
  /** Absolute URL to poll. */
  url: string;
  /** Bearer token for the widget endpoint. */
  token: string;
  /** Base interval in ms when the page is active and state is changing. */
  baseInterval: number;
  /** Upper bound when backing off. */
  maxInterval: number;
  /**
   * Invoked with (state, etag) for every 200 response. State is unknown here
   * so the caller can cast to the aggregate type it expects.
   */
  onFresh: (state: unknown, etag: string | null) => void;
  /** Optional error handler (silent by default so polling stays passive). */
  onError?: (err: unknown) => void;
  /** Optional logger hook used by tests and the widget loader in dev. */
  log?: (message: string, extra?: unknown) => void;
}

export interface SmartPollHandle {
  /** Trigger an immediate fetch and reset backoff. Safe to call often. */
  ping: () => void;
  /** Stop the loop and release timers. */
  stop: () => void;
}

export function startSmartPoll(opts: SmartPollOptions): SmartPollHandle {
  let currentInterval = opts.baseInterval;
  let unchangedStreak = 0;
  let lastEtag: string | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight = false;
  let stopped = false;
  // Codex review fix: holding the AbortController for the active fetch lets
  // `stop()` cancel network in-flight, and gives us a deterministic post-await
  // re-check — token hot-swap during destroy used to deliver the previous
  // token's payload to onFresh.
  let activeController: AbortController | null = null;

  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = (delay: number) => {
    clear();
    if (stopped) return;
    timer = setTimeout(tick, delay);
  };

  const tick = () => {
    if (stopped) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      // Pause the loop. visibilitychange handler below re-enters when the tab
      // becomes visible again, so we don't re-arm from here.
      return;
    }
    void fetchOnce();
  };

  const fetchOnce = async () => {
    if (inFlight || stopped) return;
    inFlight = true;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    activeController = controller;
    try {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${opts.token}`,
      };
      if (lastEtag) headers['If-None-Match'] = lastEtag;
      const res = await fetch(opts.url, {
        headers,
        signal: controller?.signal,
      });

      // Re-check after the await — between the fetch resolving and us
      // touching the store / state, `stop()` may have been called.
      if (stopped) return;

      if (res.status === 304) {
        unchangedStreak += 1;
        // After three consecutive "no change" responses, double the interval
        // — up to the cap. The moment we see a real change we reset.
        if (unchangedStreak >= 3) {
          currentInterval = Math.min(opts.maxInterval, currentInterval * 2);
          unchangedStreak = 0;
        }
      } else if (res.ok) {
        unchangedStreak = 0;
        currentInterval = opts.baseInterval;
        lastEtag = res.headers.get('ETag');
        const body = (await res.json()) as unknown;
        // body parsing is async too — re-check before emitting state.
        if (stopped) return;
        opts.onFresh(body, lastEtag);
      } else {
        opts.onError?.(new Error(`smart-poll ${res.status}`));
        // Back off on errors too, but don't stop — transient 5xx / 401 during
        // token hot-swap shouldn't wedge the loop permanently.
        currentInterval = Math.min(opts.maxInterval, currentInterval * 2);
      }
    } catch (err) {
      // AbortError is the expected outcome of stop() — swallow it.
      if (err instanceof Error && err.name === 'AbortError') return;
      opts.onError?.(err);
      currentInterval = Math.min(opts.maxInterval, currentInterval * 2);
    } finally {
      inFlight = false;
      if (activeController === controller) {
        activeController = null;
      }
      schedule(currentInterval);
    }
  };

  const onVisibilityChange = () => {
    if (stopped) return;
    if (document.visibilityState === 'visible') {
      // Coming back to foreground — the user is watching, so refresh now and
      // reset the cadence so the next idle stretch starts from base.
      currentInterval = opts.baseInterval;
      unchangedStreak = 0;
      schedule(0);
    } else {
      clear();
    }
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  // Kick off the first fetch immediately (skipping the base interval) so
  // widgets hydrate as soon as the loader boots.
  schedule(0);

  return {
    ping: () => {
      if (stopped) return;
      currentInterval = opts.baseInterval;
      unchangedStreak = 0;
      schedule(0);
    },
    stop: () => {
      stopped = true;
      clear();
      // Abort any in-flight fetch so we don't apply a stale token's response
      // to a fresh store after hot-swap.
      activeController?.abort();
      activeController = null;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    },
  };
}
